// VENDOR
import { ClientJS } from 'clientjs'
import { Platform } from 'quasar'
import UAParser from 'ua-parser-js'
import SHA256 from 'crypto-js/sha256'
import { load } from '@fingerprintjs/fingerprintjs'

// Personal lib
import { transformIntoISO } from '@i18n/utils/helpers'
import { DeviceTypeEnum } from '@shared/enum/device-type.enum'
import { FingerPrintDataWebApp } from '@data/dto/fingerprintdata/fingerprintdata-webapp-dto'
import { FingerPrintDataWeb } from '@data/dto/fingerprintdata/fingerprintdata-web-dto'
import { DeviceOrientationEnum } from '@shared/enum/device-orientation.enum copy'
import { getDeviceType } from './device.lib'

// ***
// See https://github.com/artem0/canvas-fingerprinting/blob/master/fingerprinting/fingerprint.js if you want to change the fingerprint strategy or write it yourself
// ***

// Fix
declare global {
  interface Window {
    indexedDB: any;
    mozIndexedDB: any;
    webkitIndexedDB: any;
    msIndexedDB: any;
    ClientJS: any;
  }
}

const getOrientation = () => {
    const isLandscape = (window.orientation === 90 || window.orientation === -90)
    const isPortrait = !isLandscape
    return isPortrait ? DeviceOrientationEnum.PORTRAIT : DeviceOrientationEnum.LANDSCAPE
}

const isIndexedDb = () => {
  // In the following line, you should include the prefixes of implementations you want to test.
  // window.indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB
  // DON'T use "var indexedDB = ..." if you're not in a function.
  // Moreover, you may need references to some window.IDB* objects:
  // window.IDBTransaction = window.IDBTransaction || window.webkitIDBTransaction || window.msIDBTransaction || { READ_WRITE: 'readwrite' } // This line should only be needed if it is needed to support the object's constants for older browsers
  // window.IDBKeyRange = window.IDBKeyRange || window.webkitIDBKeyRange || window.msIDBKeyRange
  // (Mozilla has never prefixed these objects, so we don't need window.mozIDB*)

  return !!(window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB)
}

/**
 * Generate a fingerprint based on all data except location (location is added by the backend to the final fingerprint)
 * @param returnedFingerPrintDatas
 * @returns
 */
const generateFingerPrint = function(returnedFingerPrintDatas: FingerPrintDataWebApp): string {
    const fingerPrintDataWebFP = SHA256(JSON.stringify(returnedFingerPrintDatas.fingerPrintDataWeb)) as string
    const browserFP = SHA256(JSON.stringify(returnedFingerPrintDatas.browser)) as string
    const engineFP = SHA256(JSON.stringify(returnedFingerPrintDatas.engine)) as string
    const systemFP = SHA256(JSON.stringify(returnedFingerPrintDatas.system)) as string
    const storageFP = SHA256(JSON.stringify(returnedFingerPrintDatas.storage)) as string
    const deviceFP = SHA256(JSON.stringify(returnedFingerPrintDatas.device)) as string
  
    return SHA256(fingerPrintDataWebFP + browserFP + engineFP + systemFP + storageFP + deviceFP).toString()
}

export const getFingerPrint = async (): Promise<FingerPrintDataWebApp> => {
    // Get fingerprint datas
    const fpPromise = await load(); 
    const fingerPrintDataObject = (await fpPromise.get()).components

    // Init ClientJS
    const windowClient = new ClientJS();

    // From ClientJS
    const userAgent = windowClient.getUserAgent()

    // From UA Parser
    const ua = new UAParser()
    const engine = ua.getEngine()
    const os = ua.getOS()
    const browser = ua.getBrowser()
    const device = ua.getDevice()
    const cpu = ua.getCPU()

    // Fix : remove 'duration' property (can be different on one unique device...)
    for (const k in fingerPrintDataObject) {
        fingerPrintDataObject[k] = fingerPrintDataObject[k].value
    }

    // Init FingerPrintDataWeb
    const fingerPrintDataWeb: FingerPrintDataWeb = fingerPrintDataObject;

    // Fix : remove fontPreferences property
    delete fingerPrintDataWeb.fontPreferences

    // Fix : remove canvas.text
    delete fingerPrintDataWeb.canvas.text

    // Fix : transform language in ISO language (ex: fr => fr-FR)
    fingerPrintDataWeb.languages = (fingerPrintDataWeb.languages as Array<Array<string>>).map(k => {
        return k.map(l => transformIntoISO(l))
    })

    // Custom language
    const customLanguage = windowClient.getSystemLanguage().toLowerCase()

    const returnedFingerPrintDatas: FingerPrintDataWebApp = {
        fingerprint: '',
        fingerPrintDataWeb: fingerPrintDataWeb,
        browser: {
            userAgent,
            browserName: browser.name ?? '',
            browserVersion: browser.version ?? '',
            browserMajorVersion: windowClient.getBrowserMajorVersion(),
            screenPrint: windowClient.getScreenPrint(),
            pluginList: windowClient.getPlugins()
        },
        engine, // { name: '', version: '' }
        system: {
            fontList: windowClient.getFonts(),
            systemTimeZone: windowClient.getTimeZone(),
            language: customLanguage.length > 2 ? customLanguage.substring(0, 2) : customLanguage,
            hardwareConcurrency: window.navigator.hardwareConcurrency,
            cpu, // { architecture: '' }
            os // { name: '', version: '' }
        },
        storage: {
            localStorage: windowClient.isLocalStorage(),
            sessionStorage: windowClient.isSessionStorage(),
            indexedDb: isIndexedDb(),
            cookies: windowClient.isCookie()
        },
        device: {
            model: device.model ?? '',
            type: device.type ?? '',
            vendor: device.vendor ?? '',
            deviceName: undefined,
            isMobile: Platform.is.mobile === true,
            isDesktop: Platform.is.desktop === true,
            hasTouch: Platform.has.touch === true,
            orientation: getOrientation()
        },
    }

    // Update device type
    returnedFingerPrintDatas.device.type = getDeviceType(returnedFingerPrintDatas)?.toString() ?? DeviceTypeEnum.UNKNOWN

    // Update fingerPrintWeb
    returnedFingerPrintDatas.fingerprint = generateFingerPrint(returnedFingerPrintDatas)

    return returnedFingerPrintDatas
}